<script lang="ts" setup>
import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { AlterableHeaderService } from '@/coreDomainsModule/entities/inputExcelInfo';
import {
  ConditionOperators,
  ConditionValueTypes,
  ImportOrderStatusRule,
  OrderMappingRuleCondition,
  OrderFieldMapping,
  OrderFieldMappings,
  OrderMappingField,
  OrderRowMappingRule,
  RegisterOrderExcelInfo,
  RegisterOrderExcelInfos,
  createId,
  getDataTypeByField,
} from '@/coreDomainsModule/entities/InputOrderExcelInfo';
import type { ConditionOperator, ConditionValueType, OrderFieldMappingDataType } from '@/coreDomainsModule/entities/InputOrderExcelInfo';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import { useNativeDragReorder } from '@/corePagesModule/composables/useNativeDragReorder';
import { i18n } from '@/i18n';
import { UNSELECTED_HEADER_VALUE } from '@/registerInputModule/constants';
import { computed, ref, watch } from 'vue';
import { From } from './InputExcelForm.vue';
import InputExcelOrderStatusInfo from './InputExcelOrderStatusInfo.vue';
import SelectableField from '../SelectableField.vue';
import { isRequireAlterableHeader } from '../../interfaces/LinkingInput';
import { RegisterInputViewModel } from '../../RegisterInputViewModel';
import ConditionDateInput from './ConditionDateInput.vue';

const t = i18n.t;
const vm = new RegisterInputViewModel();
const orderMappingFields = AlterableHeaderService.orderAlterableHeaderList as OrderMappingField[];
const conditionValueTypeLabels: Record<Exclude<ConditionValueType, 'none'>, string> = {
  text: t('文字列'),
  number: t('数値'),
  date: t('日付'),
};
const conditionOperatorLabels: Record<ConditionOperator, string> = Object.fromEntries(
  ConditionOperators.map((operator) => [operator, t(`condition-op-${operator}`)])
) as Record<ConditionOperator, string>;
const conditionOperatorSuggestions = ConditionOperators.map((operator) => conditionOperatorLabels[operator]);
const conditionValueTypeSuggestions = ConditionValueTypes.map((valueType) => conditionValueTypeLabels[valueType]);
const comparableConditionValueTypeSuggestions = ConditionValueTypes.filter((valueType) => valueType !== 'text').map(
  (valueType) => conditionValueTypeLabels[valueType]
);
const containsConditionValueTypeSuggestions = ConditionValueTypes.filter((valueType) => valueType !== 'date').map(
  (valueType) => conditionValueTypeLabels[valueType]
);

const props = defineProps<{
  from: From;
  selectedFieldId: string;
  suggestions: string[];
  orderExcelInfos: RegisterOrderExcelInfos;
}>();

/** 選択中の発注エクセル登録設定ID。template上では未選択になり得る。 */
type MaybeOrderExcelInfoId = string | undefined;
/** 選択中の行設定。template上では未選択になり得る。 */
type MaybeOrderRowMappingRule = OrderRowMappingRule | undefined;

const emits = defineEmits<{
  (e: 'update-selected-field-id', selectedFieldId: string): void;
  (e: 'update-order-excel-infos', orderExcelInfos: RegisterOrderExcelInfos): void;
  (e: 'remove-order-excel-info', orderExcelInfos: RegisterOrderExcelInfos): void;
  (e: 'confirm-order-excel-info-name'): void;
}>();

/**
 * 発注フィールド読み替え設定の初期値を作成する
 */
const createDefaultOrderFieldMappings = (): OrderFieldMappings => {
  return orderMappingFields.reduce<OrderFieldMappings>((fieldMappings, field) => {
    fieldMappings[field] = createColumnOrderFieldMapping(getDataTypeByField(field));
    return fieldMappings;
  }, {} as OrderFieldMappings);
};

/**
 * 列参照マッピングを作成する
 * @param dataType - データタイプ
 * @param column - 列名
 * @param unused - 不使用かどうか
 */
function createColumnOrderFieldMapping(dataType: OrderFieldMappingDataType, column = '', unused = false): OrderFieldMapping {
  return {
    dataType,
    type: 'column',
    column,
    unused,
    validateError: 'none',
  };
}

/**
 * 固定値マッピングを作成する
 * @param dataType - データタイプ
 * @param value - 固定値
 * @param unused - 不使用かどうか
 */
function createLiteralOrderFieldMapping(dataType: OrderFieldMappingDataType, value = '', unused = false): OrderFieldMapping {
  return {
    dataType,
    type: 'literal',
    value,
    unused,
    validateError: 'none',
  };
}

/**
 * 連結マッピングを作成する
 * @param dataType - データタイプ
 * @param parts - 連結する部品
 * @param unused - 不使用かどうか
 */
function createConcatOrderFieldMapping(
  dataType: OrderFieldMappingDataType,
  parts: OrderFieldMapping[] = [createColumnOrderFieldMapping(dataType)],
  unused = false
): OrderFieldMapping {
  return {
    dataType,
    type: 'concat',
    parts: parts.map((part) => normalizeOrderFieldMappingDataType(part, dataType)),
    unused,
    validateError: 'none',
  };
}

/**
 * 発注フィールドマッピングにデータタイプを補完する
 * @param mapping - 発注フィールドマッピング
 * @param dataType - データタイプ
 */
function normalizeOrderFieldMappingDataType(mapping: OrderFieldMapping, dataType: OrderFieldMappingDataType): OrderFieldMapping {
  if (mapping.type !== 'concat') return { ...mapping, dataType };
  return {
    ...mapping,
    dataType,
    parts: mapping.parts.map((part) => normalizeOrderFieldMappingDataType(part, dataType)),
  };
}

/**
 * 発注エクセル行設定の初期値を作成する
 * @param name - 行設定名
 * @param priority - 優先度
 */
const createDefaultOrderRowMappingRule = (name: string, priority: number): OrderRowMappingRule => ({
  id: createId('OMR'),
  name,
  priority,
  duplicatePriority: undefined,
  rowConditions: [],
  fieldMappings: createDefaultOrderFieldMappings(),
  orderStatusRules: [],
  fallbackOrderStatus: OrderStatus.ApprovedAwaitingDeliveryDate,
});

/**
 * 行条件の初期値を作成する
 */
const createDefaultRowCondition = (): OrderMappingRuleCondition => ({
  id: createId('OMRC'),
  header: '',
  operator: 'equals',
  valueType: 'text',
  value: '',
  validateError: {
    header: 'none',
    value: 'none',
  },
});

const selectedOrderExcelInfoId = ref<string>('');
const selectedRowMappingRuleId = ref<string>('');
const editingOrderExcelInfoId = ref<string>('');
const editingRowMappingRuleId = ref<string>('');
const expandedOrderFieldMappingIds = ref<string[]>([]);
const rowMappingRulesForDrag = ref<OrderRowMappingRule[]>([]);

watch(
  () => props.orderExcelInfos,
  (orderExcelInfos) => {
    if (orderExcelInfos.some((orderExcelInfo) => orderExcelInfo.id === selectedOrderExcelInfoId.value)) return;
    selectedOrderExcelInfoId.value = orderExcelInfos[0]?.id ?? '';
  },
  { immediate: true }
);

const selectedOrderExcelInfo = computed(() => {
  return props.orderExcelInfos.find((orderExcelInfo) => orderExcelInfo.id === selectedOrderExcelInfoId.value);
});

const selectedRowMappingRule = computed(() => {
  return rowMappingRulesForDrag.value.find((rule) => rule.id === selectedRowMappingRuleId.value);
});

/**
 * 表示する発注エクセル登録設定を選択する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 */
const selectOrderExcelInfo = (orderExcelInfoId: string): void => {
  selectedOrderExcelInfoId.value = orderExcelInfoId;
};

/**
 * 行設定を優先度順に取得する
 * @param orderExcelInfo - 発注エクセル登録設定
 */
const sortedRowMappingRules = (orderExcelInfo: RegisterOrderExcelInfo): OrderRowMappingRule[] => {
  return [...orderExcelInfo.rowMappingRules].sort((a, b) => a.priority - b.priority);
};

watch(
  selectedOrderExcelInfo,
  (orderExcelInfo) => {
    const sortedRules = orderExcelInfo ? sortedRowMappingRules(orderExcelInfo) : [];
    rowMappingRulesForDrag.value = sortedRules;

    if (sortedRules.some((rule) => rule.id === selectedRowMappingRuleId.value)) return;
    selectedRowMappingRuleId.value = sortedRules[0]?.id ?? '';
  },
  { immediate: true, deep: true }
);

/**
 * 表示する発注エクセル行設定を選択する
 * @param rowMappingRuleId - 発注エクセル行設定ID
 */
const selectRowMappingRule = (rowMappingRuleId: string): void => {
  selectedRowMappingRuleId.value = rowMappingRuleId;
};

/**
 * 発注エクセル登録設定名の編集を開始する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 */
const startEditingOrderExcelInfoName = (orderExcelInfoId: string): void => {
  editingOrderExcelInfoId.value = orderExcelInfoId;
};

/**
 * 発注エクセル登録設定名の編集を終了する
 */
const stopEditingOrderExcelInfoName = (): void => {
  if (editingOrderExcelInfoId.value && props.from === 'HomeInput') {
    emits('confirm-order-excel-info-name');
  }
  editingOrderExcelInfoId.value = '';
};

/**
 * Enterキーで発注エクセル登録設定名の編集を終了する
 * @param event - キーボードイベント
 */
const stopEditingOrderExcelInfoNameByEnter = (event: KeyboardEvent): void => {
  if (event.isComposing || event.keyCode === 229) return;
  stopEditingOrderExcelInfoName();
};

/**
 * 発注エクセル行設定名の編集を開始する
 * @param rowMappingRuleId - 発注エクセル行設定ID
 */
const startEditingRowMappingRuleName = (rowMappingRuleId: string): void => {
  editingRowMappingRuleId.value = rowMappingRuleId;
};

/**
 * 発注エクセル行設定名の編集を終了する
 */
const stopEditingRowMappingRuleName = (): void => {
  editingRowMappingRuleId.value = '';
  if (props.from === 'HomeInput') {
  }
};

/**
 * Enterキーで発注エクセル行設定名の編集を終了する
 * @param event - キーボードイベント
 */
const stopEditingRowMappingRuleNameByEnter = (event: KeyboardEvent): void => {
  if (event.isComposing || event.keyCode === 229) return;
  stopEditingRowMappingRuleName();
};

/**
 * 指定した発注エクセル登録設定を更新する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param update - 更新処理
 */
const updateOrderExcelInfo = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  update: (orderExcelInfo: RegisterOrderExcelInfo) => RegisterOrderExcelInfo
): void => {
  if (!orderExcelInfoId) return;

  emits(
    'update-order-excel-infos',
    props.orderExcelInfos.map((orderExcelInfo) => (orderExcelInfo.id === orderExcelInfoId ? update(orderExcelInfo) : orderExcelInfo))
  );
};

/**
 * 発注エクセル登録設定を削除する
 * @param orderExcelInfo - 削除する発注エクセル登録設定
 */
const removeOrderExcelInfo = (orderExcelInfo: RegisterOrderExcelInfo): void => {
  const removedIndex = props.orderExcelInfos.findIndex((currentOrderExcelInfo) => currentOrderExcelInfo.id === orderExcelInfo.id);
  const orderExcelInfos = props.orderExcelInfos.filter((currentOrderExcelInfo) => currentOrderExcelInfo.id !== orderExcelInfo.id);

  if (editingOrderExcelInfoId.value === orderExcelInfo.id) {
    editingOrderExcelInfoId.value = '';
  }

  if (selectedOrderExcelInfoId.value === orderExcelInfo.id) {
    selectedOrderExcelInfoId.value = orderExcelInfos[removedIndex]?.id ?? orderExcelInfos[removedIndex - 1]?.id ?? '';
  }

  if (props.from === 'HomeInput') {
    emits('remove-order-excel-info', orderExcelInfos);
    return;
  }

  emits('update-order-excel-infos', orderExcelInfos);
};

/**
 * 発注エクセル登録設定名を更新する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param name - 設定名
 */
const updateOrderExcelInfoName = (orderExcelInfoId: MaybeOrderExcelInfoId, name: string): void => {
  updateOrderExcelInfo(orderExcelInfoId, (orderExcelInfo) => ({
    ...orderExcelInfo,
    name,
  }));
};

/**
 * 指定した行設定を更新する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 更新後の行設定
 */
const updateOrderMappingRule = (orderExcelInfoId: MaybeOrderExcelInfoId, rule: MaybeOrderRowMappingRule): void => {
  if (!orderExcelInfoId || !rule) return;

  updateOrderExcelInfo(orderExcelInfoId, (orderExcelInfo) => ({
    ...orderExcelInfo,
    rowMappingRules: orderExcelInfo.rowMappingRules.map((rowMappingRule) => (rowMappingRule.id === rule.id ? rule : rowMappingRule)),
  }));
};

/**
 * 発注エクセル行設定名を更新する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 * @param name - 行設定名
 */
const updateOrderMappingRuleName = (orderExcelInfoId: MaybeOrderExcelInfoId, rule: MaybeOrderRowMappingRule, name: string): void => {
  if (!rule) return;

  updateOrderMappingRule(orderExcelInfoId, {
    ...rule,
    name,
  });
};

/**
 * 発注エクセル行設定を追加する
 * @param orderExcelInfo - 発注エクセル登録設定
 */
const addOrderMappingRule = (orderExcelInfo: RegisterOrderExcelInfo): void => {
  const nextPriority =
    orderExcelInfo.rowMappingRules.length > 0 ? Math.max(...orderExcelInfo.rowMappingRules.map((rule) => rule.priority)) + 1 : 0;
  const newRule = createDefaultOrderRowMappingRule(`${t('行条件')}${orderExcelInfo.rowMappingRules.length + 1}`, nextPriority);
  selectedRowMappingRuleId.value = newRule.id;

  updateOrderExcelInfo(orderExcelInfo.id, (currentOrderExcelInfo) => ({
    ...currentOrderExcelInfo,
    rowMappingRules: [...currentOrderExcelInfo.rowMappingRules, newRule],
  }));
};

/**
 * 発注エクセル行設定を削除する
 * @param orderExcelInfo - 発注エクセル登録設定
 * @param rule - 削除する行設定
 */
const removeOrderMappingRule = (orderExcelInfo: RegisterOrderExcelInfo, rule: OrderRowMappingRule): void => {
  const sortedRules = sortedRowMappingRules(orderExcelInfo);
  const removedIndex = sortedRules.findIndex((rowMappingRule) => rowMappingRule.id === rule.id);
  const rowMappingRules = sortedRules
    .filter((rowMappingRule) => rowMappingRule.id !== rule.id)
    .map((rowMappingRule, index) => ({
      ...rowMappingRule,
      priority: index,
    }));

  if (editingRowMappingRuleId.value === rule.id) {
    editingRowMappingRuleId.value = '';
  }

  if (selectedRowMappingRuleId.value === rule.id) {
    selectedRowMappingRuleId.value = rowMappingRules[removedIndex]?.id ?? rowMappingRules[removedIndex - 1]?.id ?? '';
  }

  updateOrderExcelInfo(orderExcelInfo.id, (currentOrderExcelInfo) => ({
    ...currentOrderExcelInfo,
    rowMappingRules,
  }));
};

/**
 * 発注エクセル行設定のDnDキーを取得する
 * @param rule - 発注エクセル行設定
 */
const getRowMappingRuleKey = (rule: OrderRowMappingRule): string => rule.id;

/**
 * 行設定を並び順に合わせて優先度更新する
 */
const handleRowMappingRulesReorder = (): void => {
  const orderExcelInfo = selectedOrderExcelInfo.value;
  if (!orderExcelInfo) return;

  const rowMappingRules = rowMappingRulesForDrag.value.map((rule, index) => ({
    ...rule,
    priority: index,
  }));
  rowMappingRulesForDrag.value = rowMappingRules;

  updateOrderExcelInfo(orderExcelInfo.id, (currentOrderExcelInfo) => ({
    ...currentOrderExcelInfo,
    rowMappingRules,
  }));
};

const {
  handleDragStart: handleRowMappingRuleDragStart,
  handleDragEnd: handleRowMappingRuleDragEnd,
  handleDragOver: handleRowMappingRuleDragOver,
  handleDragLeave: handleRowMappingRuleDragLeave,
  handleDragOverEnd: handleRowMappingRuleDragOverEnd,
  handleDragLeaveEnd: handleRowMappingRuleDragLeaveEnd,
  handleDrop: handleRowMappingRuleDrop,
  handleDropToEnd: handleRowMappingRuleDropToEnd,
  isDragging: isRowMappingRuleDragging,
  isDropTarget: isRowMappingRuleDropTarget,
  isDragOverEnd: isRowMappingRuleDragOverEnd,
} = useNativeDragReorder({
  items: rowMappingRulesForDrag,
  getKey: getRowMappingRuleKey,
  onReorder: handleRowMappingRulesReorder,
});

/**
 * 発注マッピング式を画面表示用の読み替え値に変換する
 * @param mapping - 発注マッピング式
 */
const orderMappingToValue = (mapping?: OrderFieldMapping): string => {
  if (!mapping) return '';
  switch (mapping.type) {
    case 'column':
      return mapping.column;
    case 'literal':
      return mapping.value;
    case 'concat':
      return mapping.parts.map(orderMappingToValue).join('');
  }
};

/**
 * 発注マッピング式を画面表示用のサマリに変換する
 * @param mapping - 発注マッピング式
 */
const orderMappingToSummary = (mapping?: OrderFieldMapping): string => {
  if (!mapping) return '';
  switch (mapping.type) {
    case 'column':
      return mapping.column;
    case 'literal':
      return mapping.value;
    case 'concat':
      return mapping.parts.map((part) => orderMappingToSummary(part) || t('未設定')).join(' + ');
  }
};

/**
 * 発注フィールド詳細設定の開閉キーを取得する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param ruleId - 行設定ID
 * @param field - 発注フィールド
 */
const getOrderFieldMappingDetailKey = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  ruleId: string | undefined,
  field: OrderMappingField
): string => {
  return `${orderExcelInfoId}::${ruleId}::${field}`;
};

/**
 * 発注フィールド詳細設定が開いているかどうか
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param ruleId - 行設定ID
 * @param field - 発注フィールド
 */
const isOrderFieldMappingDetailOpen = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  ruleId: string | undefined,
  field: OrderMappingField
): boolean => {
  if (!orderExcelInfoId || !ruleId) return false;
  return expandedOrderFieldMappingIds.value.includes(getOrderFieldMappingDetailKey(orderExcelInfoId, ruleId, field));
};

/**
 * 発注フィールド詳細設定の開閉を切り替える
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param ruleId - 行設定ID
 * @param field - 発注フィールド
 */
const toggleOrderFieldMappingDetail = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  ruleId: string | undefined,
  field: OrderMappingField
): void => {
  if (!orderExcelInfoId || !ruleId) return;

  const key = getOrderFieldMappingDetailKey(orderExcelInfoId, ruleId, field);
  expandedOrderFieldMappingIds.value = expandedOrderFieldMappingIds.value.includes(key)
    ? expandedOrderFieldMappingIds.value.filter((expandedKey) => expandedKey !== key)
    : [...expandedOrderFieldMappingIds.value, key];
};

/**
 * 発注フィールドの読み替え設定を更新する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 * @param field - 発注フィールド
 * @param updateValue - 更新値
 */
const updateOrderFieldMapping = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  rule: MaybeOrderRowMappingRule,
  field: OrderMappingField,
  updateValue: string
): void => {
  if (!rule) return;

  const column = updateValue !== UNSELECTED_HEADER_VALUE ? updateValue : '';
  const dataType = rule.fieldMappings[field]?.dataType ?? getDataTypeByField(field);

  updateOrderMappingRule(orderExcelInfoId, {
    ...rule,
    fieldMappings: {
      ...rule.fieldMappings,
      [field]: {
        dataType,
        type: 'column',
        column,
        unused: rule.fieldMappings[field]?.unused ?? false,
        validateError: 'none',
      },
    },
  });
};

/**
 * 発注フィールドマッピングの種別を取得する
 * @param mapping - 発注フィールドマッピング
 */
const getOrderFieldMappingType = (mapping?: OrderFieldMapping): OrderFieldMapping['type'] => mapping?.type ?? 'column';

/**
 * 発注フィールドマッピングのデータタイプを取得する
 * @param mapping - 発注フィールドマッピング
 * @param field - 発注フィールド
 */
const getOrderFieldMappingDataType = (mapping: OrderFieldMapping | undefined, field: OrderMappingField): OrderFieldMappingDataType => {
  return mapping?.dataType ?? getDataTypeByField(field);
};

/**
 * 固定値マッピング用inputのtypeを取得する
 * @param mapping - 発注フィールドマッピング
 * @param field - 発注フィールド
 */
const getOrderFieldLiteralInputType = (mapping: OrderFieldMapping | undefined, field: OrderMappingField): 'text' | 'number' => {
  return getOrderFieldMappingDataType(mapping, field) === 'number' ? 'number' : 'text';
};

/**
 * inputイベントから文字列値を取得する
 * @param event - 入力イベント
 */
const getInputValue = (event: Event): string => (event.target instanceof HTMLInputElement ? event.target.value : '');

/**
 * selectイベントから文字列値を取得する
 * @param event - 変更イベント
 */
const getSelectValue = (event: Event): string => (event.target instanceof HTMLSelectElement ? event.target.value : '');

/**
 * 発注フィールドマッピングの種別を更新する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 * @param field - 発注フィールド
 * @param mappingType - マッピング種別
 */
const updateOrderFieldMappingType = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  rule: MaybeOrderRowMappingRule,
  field: OrderMappingField,
  mappingType: string
): void => {
  if (!rule) return;
  if (mappingType !== 'column' && mappingType !== 'literal' && mappingType !== 'concat') return;

  const currentMapping = rule.fieldMappings[field];
  const dataType = currentMapping?.dataType ?? getDataTypeByField(field);
  const unused = currentMapping?.unused ?? false;
  const nextMapping =
    mappingType === 'column'
      ? createColumnOrderFieldMapping(dataType, currentMapping?.type === 'column' ? currentMapping.column : '', unused)
      : mappingType === 'literal'
      ? createLiteralOrderFieldMapping(dataType, currentMapping?.type === 'literal' ? currentMapping.value : '', unused)
      : createConcatOrderFieldMapping(
          dataType,
          currentMapping?.type === 'concat' ? currentMapping.parts : [currentMapping ?? createColumnOrderFieldMapping(dataType)],
          unused
        );

  updateOrderMappingRule(orderExcelInfoId, {
    ...rule,
    fieldMappings: {
      ...rule.fieldMappings,
      [field]: nextMapping,
    },
  });
};

/**
 * 固定値マッピングを更新する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 * @param field - 発注フィールド
 * @param value - 固定値
 */
const updateOrderFieldLiteralMapping = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  rule: MaybeOrderRowMappingRule,
  field: OrderMappingField,
  value: string
): void => {
  if (!rule) return;

  const dataType = rule.fieldMappings[field]?.dataType ?? getDataTypeByField(field);
  updateOrderMappingRule(orderExcelInfoId, {
    ...rule,
    fieldMappings: {
      ...rule.fieldMappings,
      [field]: createLiteralOrderFieldMapping(dataType, value, rule.fieldMappings[field]?.unused ?? false),
    },
  });
};

/**
 * 連結マッピングの部品を更新する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 * @param field - 発注フィールド
 * @param parts - 更新後の部品
 */
const updateOrderFieldConcatParts = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  rule: MaybeOrderRowMappingRule,
  field: OrderMappingField,
  parts: OrderFieldMapping[]
): void => {
  if (!rule) return;

  const dataType = rule.fieldMappings[field]?.dataType ?? getDataTypeByField(field);
  updateOrderMappingRule(orderExcelInfoId, {
    ...rule,
    fieldMappings: {
      ...rule.fieldMappings,
      [field]: createConcatOrderFieldMapping(dataType, parts, rule.fieldMappings[field]?.unused ?? false),
    },
  });
};

/**
 * 連結マッピングを取得する
 * @param mapping - 発注フィールドマッピング
 */
const getConcatOrderFieldMapping = (mapping?: OrderFieldMapping): OrderFieldMapping[] => {
  return mapping?.type === 'concat' ? mapping.parts : [];
};

/**
 * 連結部品の種別を更新する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 * @param field - 発注フィールド
 * @param partIndex - 部品index
 * @param partType - 連結部品の種別
 */
const updateConcatPartType = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  rule: MaybeOrderRowMappingRule,
  field: OrderMappingField,
  partIndex: number,
  partType: string
): void => {
  if (!rule) return;

  const dataType = rule.fieldMappings[field]?.dataType ?? getDataTypeByField(field);
  if (partType !== 'column' && partType !== 'literal') return;
  const parts = getConcatOrderFieldMapping(rule.fieldMappings[field]).map((part, index) =>
    index === partIndex
      ? partType === 'column'
        ? createColumnOrderFieldMapping(dataType)
        : createLiteralOrderFieldMapping(dataType)
      : part
  );
  updateOrderFieldConcatParts(orderExcelInfoId, rule, field, parts);
};

/**
 * 連結部品の列名を更新する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 * @param field - 発注フィールド
 * @param partIndex - 部品index
 * @param column - 列名
 */
const updateConcatPartColumn = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  rule: MaybeOrderRowMappingRule,
  field: OrderMappingField,
  partIndex: number,
  column: string
): void => {
  if (!rule) return;

  const dataType = rule.fieldMappings[field]?.dataType ?? getDataTypeByField(field);
  const normalizedColumn = column !== UNSELECTED_HEADER_VALUE ? column : '';
  const parts = getConcatOrderFieldMapping(rule.fieldMappings[field]).map((part, index) =>
    index === partIndex ? createColumnOrderFieldMapping(dataType, normalizedColumn) : part
  );
  updateOrderFieldConcatParts(orderExcelInfoId, rule, field, parts);
};

/**
 * 連結部品の固定値を更新する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 * @param field - 発注フィールド
 * @param partIndex - 部品index
 * @param value - 固定値
 */
const updateConcatPartLiteral = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  rule: MaybeOrderRowMappingRule,
  field: OrderMappingField,
  partIndex: number,
  value: string
): void => {
  if (!rule) return;

  const dataType = rule.fieldMappings[field]?.dataType ?? getDataTypeByField(field);
  const parts = getConcatOrderFieldMapping(rule.fieldMappings[field]).map((part, index) =>
    index === partIndex ? createLiteralOrderFieldMapping(dataType, value) : part
  );
  updateOrderFieldConcatParts(orderExcelInfoId, rule, field, parts);
};

/**
 * 連結部品を追加する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 * @param field - 発注フィールド
 * @param partType - 追加する部品種別
 */
const addConcatPart = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  rule: MaybeOrderRowMappingRule,
  field: OrderMappingField,
  partType: 'column' | 'literal'
): void => {
  if (!rule) return;

  const dataType = rule.fieldMappings[field]?.dataType ?? getDataTypeByField(field);
  updateOrderFieldConcatParts(orderExcelInfoId, rule, field, [
    ...getConcatOrderFieldMapping(rule.fieldMappings[field]),
    partType === 'column' ? createColumnOrderFieldMapping(dataType) : createLiteralOrderFieldMapping(dataType),
  ]);
};

/**
 * 連結部品を削除する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 * @param field - 発注フィールド
 * @param partIndex - 削除する部品index
 */
const removeConcatPart = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  rule: MaybeOrderRowMappingRule,
  field: OrderMappingField,
  partIndex: number
): void => {
  if (!rule) return;

  updateOrderFieldConcatParts(
    orderExcelInfoId,
    rule,
    field,
    getConcatOrderFieldMapping(rule.fieldMappings[field]).filter((_, index) => index !== partIndex)
  );
};

/**
 * 発注フィールドの不使用設定を更新する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 * @param field - 発注フィールド
 * @param unused - 不使用かどうか
 */
const updateOrderFieldUnused = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  rule: MaybeOrderRowMappingRule,
  field: OrderMappingField,
  unused: boolean
): void => {
  if (!rule) return;

  const currentMapping = rule.fieldMappings[field];

  updateOrderMappingRule(orderExcelInfoId, {
    ...rule,
    fieldMappings: {
      ...rule.fieldMappings,
      [field]: currentMapping
        ? {
            ...normalizeOrderFieldMappingDataType(currentMapping, currentMapping.dataType ?? getDataTypeByField(field)),
            unused,
            validateError: 'none',
          }
        : {
            dataType: getDataTypeByField(field),
            type: 'column',
            column: '',
            unused,
            validateError: 'none',
          },
    },
  });
};

/**
 * 発注フィールドマッピングの入力エラーメッセージを取得する
 * @param mapping - 発注フィールドマッピング
 */
const getOrderFieldMappingErrorMessage = (mapping?: OrderFieldMapping): string => {
  if (mapping?.validateError === 'notEmpty') return t('記入必須です');
  return '';
};

/**
 * 大小比較を行う条件かどうかを判定する
 * @param operator - 条件演算子
 */
const isComparableOperator = (operator: ConditionOperator): boolean => ['gt', 'gte', 'lt', 'lte'].includes(operator);

/**
 * 部分一致を行う条件かどうかを判定する
 * @param operator - 条件演算子
 */
const isContainsOperator = (operator: ConditionOperator): boolean => ['contains', 'not_contains'].includes(operator);

/**
 * 値入力が不要な条件かどうかを判定する
 * @param operator - 条件演算子
 */
const isNoValueOperator = (operator: ConditionOperator): boolean => operator === 'is_empty' || operator === 'is_not_empty';

/**
 * 条件演算子の表示値を取得する
 * @param operator - 条件演算子
 */
const getOperatorDisplayValue = (operator: ConditionOperator): string => conditionOperatorLabels[operator];

/**
 * 条件演算子の表示名から内部値を取得する
 * @param displayValue - 表示値
 */
const getOperatorByDisplayValue = (displayValue: string): ConditionOperator => {
  return ConditionOperators.find((operator) => conditionOperatorLabels[operator] === displayValue) ?? 'equals';
};

/**
 * 条件値の入力種別表示値を取得する
 * @param valueType - 条件値の入力種別
 */
const getValueTypeDisplayValue = (valueType: ConditionValueType): string => {
  if (valueType === 'none') return '';
  return conditionValueTypeLabels[valueType];
};

/**
 * 条件値の入力種別表示名から内部値を取得する
 * @param displayValue - 表示値
 */
const getValueTypeByDisplayValue = (displayValue: string): Exclude<ConditionValueType, 'none'> => {
  return ConditionValueTypes.find((valueType) => conditionValueTypeLabels[valueType] === displayValue) ?? 'text';
};

/**
 * 条件演算子に応じた条件値種別候補を取得する
 * @param operator - 条件演算子
 */
const getConditionValueTypeSuggestions = (operator: ConditionOperator): string[] => {
  if (isComparableOperator(operator)) return comparableConditionValueTypeSuggestions;
  if (isContainsOperator(operator)) return containsConditionValueTypeSuggestions;
  return conditionValueTypeSuggestions;
};

/**
 * 行条件の値エラー文言を取得する
 * @param condition - 行条件
 */
const getRowConditionValueErrorMessage = (condition: OrderMappingRuleCondition): string => {
  if (condition.validateError?.value === 'notEmpty') return t('記入必須です');
  return '　';
};

/**
 * 行条件を重複判定用の文字列に変換する
 * @param condition - 行条件
 */
const serializeRowCondition = (condition: OrderMappingRuleCondition): string => {
  const value = condition.value == null ? '' : String(condition.value);
  return [condition.header, condition.operator, condition.valueType, value].join('\u001f');
};

/**
 * 行設定の行条件全体を重複判定用の文字列に変換する
 * @param rule - 行設定
 */
const serializeRowConditions = (rule: OrderRowMappingRule): string => {
  if (rule.rowConditions.length === 0) return '__all_rows__';
  return rule.rowConditions.map(serializeRowCondition).sort().join('\u001e');
};

/**
 * 同じ行設定内に同じ条件行があるかどうかを判定する
 * @param rule - 行設定
 */
const hasDuplicatedRowCondition = (rule: OrderRowMappingRule): boolean => {
  const signatures = new Set<string>();
  return rule.rowConditions.some((condition) => {
    const signature = serializeRowCondition(condition);
    if (signatures.has(signature)) return true;
    signatures.add(signature);
    return false;
  });
};

/**
 * 同じ全体設定内の別行設定と行条件全体が重複しているかどうかを判定する
 * @param orderExcelInfo - 発注エクセル登録設定
 * @param rule - 行設定
 */
const hasDuplicatedRowConditionsRule = (orderExcelInfo: RegisterOrderExcelInfo, rule: OrderRowMappingRule): boolean => {
  const signature = serializeRowConditions(rule);
  return orderExcelInfo.rowMappingRules.some(
    (rowMappingRule) => rowMappingRule.id !== rule.id && serializeRowConditions(rowMappingRule) === signature
  );
};

/**
 * 行条件重複エラー文言を取得する
 * @param orderExcelInfo - 発注エクセル登録設定
 * @param rule - 行設定
 */
const getRowConditionDuplicatedErrorMessage = (
  orderExcelInfo: RegisterOrderExcelInfo | undefined,
  rule: MaybeOrderRowMappingRule
): string => {
  if (!orderExcelInfo || !rule) return '';

  if (hasDuplicatedRowConditionsRule(orderExcelInfo, rule)) return t('他の行設定と行の条件が重複しています');
  if (hasDuplicatedRowCondition(rule)) return t('同じ条件行が重複しています');
  return '';
};

/**
 * 行条件数の表示文言を取得する
 * @param rule - 行設定
 */
const getRowConditionSummary = (rule: MaybeOrderRowMappingRule): string => {
  if (!rule) return '';
  if (rule.rowConditions.length === 0) return t('全ての行に適用');
  return `${t('条件数')}: ${rule.rowConditions.length}`;
};

/**
 * 行条件を更新する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 * @param conditions - 更新後の行条件
 */
const updateRowConditions = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  rule: MaybeOrderRowMappingRule,
  conditions: OrderMappingRuleCondition[]
): void => {
  if (!rule) return;

  updateOrderMappingRule(orderExcelInfoId, {
    ...rule,
    rowConditions: conditions,
  });
};

/**
 * 行条件を追加する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 */
const addRowCondition = (orderExcelInfoId: MaybeOrderExcelInfoId, rule: MaybeOrderRowMappingRule): void => {
  if (!rule) return;

  updateRowConditions(orderExcelInfoId, rule, [...rule.rowConditions, createDefaultRowCondition()]);
};

/**
 * 行条件を削除する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 * @param conditionIndex - 削除する条件index
 */
const removeRowCondition = (orderExcelInfoId: MaybeOrderExcelInfoId, rule: MaybeOrderRowMappingRule, conditionIndex: number): void => {
  if (!rule) return;

  updateRowConditions(
    orderExcelInfoId,
    rule,
    rule.rowConditions.filter((_, index) => index !== conditionIndex)
  );
};

/**
 * 行条件を部分更新する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 * @param conditionIndex - 更新する条件index
 * @param patch - 更新内容
 */
const updateRowConditionByIndex = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  rule: MaybeOrderRowMappingRule,
  conditionIndex: number,
  patch: Partial<OrderMappingRuleCondition>
): void => {
  if (!rule) return;

  const conditions = rule.rowConditions.map((condition, index) =>
    index === conditionIndex
      ? {
          ...condition,
          ...patch,
          validateError: {
            header: patch.header != null ? 'none' : condition.validateError?.header ?? 'none',
            value: patch.value !== undefined ? 'none' : condition.validateError?.value ?? 'none',
          },
        }
      : condition
  );

  updateRowConditions(orderExcelInfoId, rule, conditions);
};

/**
 * 行条件の演算子を更新する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 * @param conditionIndex - 更新する条件index
 * @param displayValue - 条件演算子表示値
 */
const updateRowConditionOperator = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  rule: MaybeOrderRowMappingRule,
  conditionIndex: number,
  displayValue: string
): void => {
  if (!rule) return;

  const currentCondition = rule.rowConditions[conditionIndex];
  if (!currentCondition) return;
  const operator = getOperatorByDisplayValue(displayValue);

  const condition: OrderMappingRuleCondition = {
    ...currentCondition,
    operator,
    validateError: {
      ...currentCondition.validateError,
      value: 'none',
    },
  };

  if (isNoValueOperator(operator)) {
    condition.valueType = 'none';
    condition.value = null;
  } else if (condition.valueType === 'none') {
    condition.valueType = 'text';
    condition.value = '';
  } else if (isComparableOperator(operator) && condition.valueType === 'text') {
    condition.valueType = 'number';
    condition.value = null;
  } else if (isContainsOperator(operator) && condition.valueType === 'date') {
    condition.valueType = 'text';
    condition.value = String(condition.value ?? '');
  }

  updateRowConditionByIndex(orderExcelInfoId, rule, conditionIndex, condition);
};

/**
 * 行条件の値種別を更新する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 * @param conditionIndex - 更新する条件index
 * @param displayValue - 値種別表示値
 */
const updateRowConditionValueType = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  rule: MaybeOrderRowMappingRule,
  conditionIndex: number,
  displayValue: string
): void => {
  if (!rule) return;

  const valueType = getValueTypeByDisplayValue(displayValue);
  updateRowConditionByIndex(orderExcelInfoId, rule, conditionIndex, {
    valueType,
    value: valueType === 'number' ? null : '',
  });
};

/**
 * 発注ステータス変換ルールを取得する
 * @param rule - 行設定
 * @returns 発注ステータス変換ルール
 */
const getImportOrderStatusRules = (rule: MaybeOrderRowMappingRule): ImportOrderStatusRule[] => rule?.orderStatusRules ?? [];

/**
 * 発注ステータス変換ルールを更新する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 * @param rules - 発注ステータス変換ルール
 */
const updateImportOrderStatusRules = (
  orderExcelInfoId: MaybeOrderExcelInfoId,
  rule: MaybeOrderRowMappingRule,
  rules: ImportOrderStatusRule[]
): void => {
  if (!rule) return;

  updateOrderMappingRule(orderExcelInfoId, {
    ...rule,
    orderStatusRules: rules,
  });
};

/**
 * 条件に一致しない場合の発注ステータスを取得する
 * @param rule - 行設定
 * @returns 条件に一致しない場合の発注ステータス
 */
const getFallbackOrderStatus = (rule: MaybeOrderRowMappingRule): OrderStatus =>
  rule?.fallbackOrderStatus ?? OrderStatus.ApprovedAwaitingDeliveryDate;

/**
 * 条件に一致しない場合の発注ステータスを更新する
 * @param orderExcelInfoId - 発注エクセル登録設定ID
 * @param rule - 行設定
 * @param status - 発注ステータス
 */
const updateFallbackOrderStatus = (orderExcelInfoId: MaybeOrderExcelInfoId, rule: MaybeOrderRowMappingRule, status: OrderStatus): void => {
  if (!rule) return;

  updateOrderMappingRule(orderExcelInfoId, {
    ...rule,
    fallbackOrderStatus: status,
  });
};

const isEditing = computed(() => props.from === 'LinkingInput');

/**
 * 必須フィールドをunusedにした時のテキストのi18nキーを返す
 * @param dataType - データタイプ
 */
const getUnusedTextI18nKey = (dataType?: OrderFieldMappingDataType) => {
  switch (dataType) {
    case 'number':
      return '取引紐付け：文言不使用（数値）';
    case 'date':
      return '取引紐付け：文言不使用（日付）';
    case 'string':
    default:
      return '取引紐付け：文言不使用';
  }
};
</script>

<template>
  <div class="linking-input__linking-container linking-container">
    <div class="linking-container__tabs">
      <div
        v-for="orderExcelInfo in orderExcelInfos"
        :key="`order-excel-info-tab-${orderExcelInfo.id}`"
        class="linking-container__tab"
        :class="{
          'linking-container__tab--editing': isEditing,
          'linking-container__tab--deletable': from === 'HomeInput',
          'linking-container__tab--active': orderExcelInfo.id === selectedOrderExcelInfoId,
        }"
      >
        <input
          v-if="editingOrderExcelInfoId === orderExcelInfo.id"
          :value="orderExcelInfo.name"
          type="text"
          class="linking-container__tab-input"
          @input="updateOrderExcelInfoName(orderExcelInfo.id, getInputValue($event))"
          @blur="stopEditingOrderExcelInfoName"
          @keydown.enter="stopEditingOrderExcelInfoNameByEnter"
        />
        <button v-else type="button" class="linking-container__tab-label" @click="selectOrderExcelInfo(orderExcelInfo.id)">
          {{ orderExcelInfo.name }}
        </button>
        <button
          v-if="editingOrderExcelInfoId === orderExcelInfo.id"
          type="button"
          class="linking-container__tab-icon-button"
          :aria-label="t('設定名の編集を完了')"
          @click="stopEditingOrderExcelInfoName"
        >
          <Mdi icon="check" />
        </button>
        <button
          v-else-if="props.from !== 'FinishInput'"
          type="button"
          class="linking-container__tab-icon-button"
          :aria-label="t('設定名を編集')"
          @click="startEditingOrderExcelInfoName(orderExcelInfo.id)"
        >
          <Mdi icon="edit" />
        </button>
        <button
          v-if="from === 'HomeInput'"
          type="button"
          class="linking-container__tab-icon-button linking-container__tab-icon-button--danger"
          :aria-label="t('全体設定を削除')"
          @click="removeOrderExcelInfo(orderExcelInfo)"
        >
          <Mdi icon="delete" variant="outlined" />
        </button>
      </div>
    </div>

    <template v-if="selectedOrderExcelInfo != null">
      <div class="linking-container__setting">
        <div class="linking-container__row-tabs">
          <div
            v-for="rule in rowMappingRulesForDrag"
            :key="`order-rule-tab-${selectedOrderExcelInfo?.id}-${rule.id}`"
            class="linking-container__row-tab"
            :class="{
              'linking-container__row-tab--editing': isEditing,
              'linking-container__row-tab--active': rule.id === selectedRowMappingRuleId,
              'linking-container__row-tab--dragging': isRowMappingRuleDragging(rule),
              'linking-container__row-tab--drop-target': isRowMappingRuleDropTarget(rule),
            }"
            :draggable="isEditing"
            @dragstart="handleRowMappingRuleDragStart($event, rule)"
            @dragend="handleRowMappingRuleDragEnd"
            @dragover="handleRowMappingRuleDragOver($event, rule)"
            @dragleave="handleRowMappingRuleDragLeave($event, rule)"
            @drop="handleRowMappingRuleDrop($event, rule)"
          >
            <input
              v-if="editingRowMappingRuleId === rule.id"
              :value="rule.name"
              type="text"
              class="linking-container__tab-input"
              draggable="false"
              @input="updateOrderMappingRuleName(selectedOrderExcelInfo?.id, rule, getInputValue($event))"
              @blur="stopEditingRowMappingRuleName"
              @keydown.enter="stopEditingRowMappingRuleNameByEnter"
            />
            <button v-else type="button" class="linking-container__tab-label" @click="selectRowMappingRule(rule.id)">
              {{ rule.name }}
            </button>
            <button
              v-if="editingRowMappingRuleId === rule.id"
              type="button"
              class="linking-container__tab-icon-button"
              :aria-label="t('行設定名の編集を完了')"
              @click="stopEditingRowMappingRuleName"
            >
              <Mdi icon="check" />
            </button>
            <button
              v-else-if="isEditing"
              type="button"
              class="linking-container__tab-icon-button"
              :aria-label="t('行設定名を編集')"
              @click="startEditingRowMappingRuleName(rule.id)"
            >
              <Mdi icon="edit" />
            </button>
            <button
              v-if="isEditing"
              type="button"
              class="linking-container__tab-icon-button linking-container__tab-icon-button--danger"
              :aria-label="t('行設定を削除')"
              draggable="false"
              :disabled="selectedOrderExcelInfo.rowMappingRules.length <= 1"
              @click="removeOrderMappingRule(selectedOrderExcelInfo, rule)"
            >
              <Mdi icon="delete" variant="outlined" />
            </button>
          </div>
          <button
            v-if="isEditing"
            type="button"
            class="linking-container__row-tab-add-button"
            :aria-label="t('行設定を追加')"
            @click="addOrderMappingRule(selectedOrderExcelInfo)"
          >
            <Mdi icon="add" />
          </button>
          <div
            class="linking-container__row-tab-end-drop"
            :class="{ 'linking-container__row-tab-end-drop--active': isRowMappingRuleDragOverEnd }"
            @dragover="handleRowMappingRuleDragOverEnd"
            @dragleave="handleRowMappingRuleDragLeaveEnd"
            @drop="handleRowMappingRuleDropToEnd"
          />
        </div>

        <template v-if="selectedRowMappingRule != null">
          <div class="linking-container__rule">
            <div class="row-conditions">
              <div class="row-conditions__header">
                <div class="row-conditions__title-wrap">
                  <h3 class="row-conditions__title">{{ t('行の条件設定') }}</h3>
                  <span class="row-conditions__summary">
                    {{ getRowConditionSummary(selectedRowMappingRule) }}
                  </span>
                </div>
                <button
                  v-if="isEditing"
                  type="button"
                  class="row-conditions__add-button"
                  @click="addRowCondition(selectedOrderExcelInfo?.id, selectedRowMappingRule)"
                >
                  <Mdi icon="add" />
                  {{ t('条件行を追加') }}
                </button>
              </div>
              <span
                v-if="getRowConditionDuplicatedErrorMessage(selectedOrderExcelInfo, selectedRowMappingRule)"
                class="row-conditions__duplicate-error"
              >
                {{ getRowConditionDuplicatedErrorMessage(selectedOrderExcelInfo, selectedRowMappingRule) }}
              </span>
              <div v-if="selectedRowMappingRule.rowConditions.length > 0" class="row-conditions__body">
                <div
                  v-for="(condition, conditionIndex) in selectedRowMappingRule.rowConditions"
                  :key="condition.id"
                  class="row-conditions__row"
                >
                  <div class="row-conditions__join-label">
                    {{ conditionIndex === 0 ? '' : t('かつ') }}
                  </div>
                  <div class="row-conditions__select-input">
                    <SelectableField
                      v-if="isEditing"
                      :value="condition.header || undefined"
                      :selected-id="condition.header"
                      :hint="t('項目を選択')"
                      :suggestions="suggestions"
                      @update-value="
                        updateRowConditionByIndex(selectedOrderExcelInfo?.id, selectedRowMappingRule, conditionIndex, { header: $event })
                      "
                    />
                    <span v-else class="row-conditions__readonly-text">{{ condition.header }}</span>
                    <span v-if="isEditing" class="row-conditions__error-text">{{
                      condition.validateError?.header === 'notEmpty' ? t('記入必須です') : '　'
                    }}</span>
                  </div>
                  <div class="row-conditions__select-input">
                    <SelectableField
                      v-if="isEditing"
                      :value="getOperatorDisplayValue(condition.operator)"
                      :selected-id="`${condition.id}-operator`"
                      :suggestions="conditionOperatorSuggestions"
                      @update-value="updateRowConditionOperator(selectedOrderExcelInfo?.id, selectedRowMappingRule, conditionIndex, $event)"
                    />
                    <span v-else class="row-conditions__readonly-text">{{ getOperatorDisplayValue(condition.operator) }}</span>
                    <span v-if="isEditing" class="row-conditions__error-text">{{ '　' }}</span>
                  </div>
                  <div class="row-conditions__value-cell">
                    <span v-if="!isEditing && isNoValueOperator(condition.operator)" class="row-conditions__readonly-text">--</span>
                    <span v-else-if="!isEditing" class="row-conditions__readonly-text">{{ condition.value ?? '' }}</span>
                    <div v-else-if="isNoValueOperator(condition.operator)" class="row-conditions__no-value">--</div>
                    <template v-else>
                      <div class="row-conditions__select-input">
                        <SelectableField
                          :value="getValueTypeDisplayValue(condition.valueType)"
                          :selected-id="`${condition.id}-value-type`"
                          :suggestions="getConditionValueTypeSuggestions(condition.operator)"
                          @update-value="
                            updateRowConditionValueType(selectedOrderExcelInfo?.id, selectedRowMappingRule, conditionIndex, $event)
                          "
                        />
                      </div>
                      <input
                        v-if="condition.valueType === 'number'"
                        :value="condition.value ?? ''"
                        class="row-conditions__input"
                        type="number"
                        :placeholder="t('入力してください')"
                        @change="
                          updateRowConditionByIndex(selectedOrderExcelInfo?.id, selectedRowMappingRule, conditionIndex, {
                            value: getInputValue($event) === '' ? null : Number(getInputValue($event)),
                          })
                        "
                      />
                      <ConditionDateInput
                        v-else-if="condition.valueType === 'date'"
                        :model-value="condition.value"
                        @update:model-value="
                          updateRowConditionByIndex(selectedOrderExcelInfo?.id, selectedRowMappingRule, conditionIndex, { value: $event })
                        "
                      />
                      <input
                        v-else
                        :value="condition.value ?? ''"
                        class="row-conditions__input"
                        type="text"
                        :placeholder="t('入力してください')"
                        @change="
                          updateRowConditionByIndex(selectedOrderExcelInfo?.id, selectedRowMappingRule, conditionIndex, {
                            value: getInputValue($event),
                          })
                        "
                      />
                    </template>
                    <span v-if="isEditing" class="row-conditions__error-text">{{ getRowConditionValueErrorMessage(condition) }}</span>
                  </div>
                  <div class="row-conditions__actions">
                    <button
                      v-if="isEditing"
                      type="button"
                      class="row-conditions__icon-button"
                      :aria-label="t('条件削除')"
                      @click="removeRowCondition(selectedOrderExcelInfo?.id, selectedRowMappingRule, conditionIndex)"
                    >
                      <Mdi icon="close" />
                    </button>
                  </div>
                </div>
              </div>
              <div v-else class="row-conditions__empty">
                <span>{{ t('条件なしの場合は全ての行に適用されます') }}</span>
              </div>
            </div>
            <div class="linking-container__left">
              <template
                v-for="field in orderMappingFields"
                :key="`order-field-${selectedOrderExcelInfo?.id}-${selectedRowMappingRule?.id}-${field}`"
              >
                <div class="linking-container__at">
                  <div class="linking-container__at--content">
                    <div class="linking-container__at--label label-container">
                      <span class="linking-container__at--label">{{ vm.displayHeaderTitle(field) }}</span>
                      <template v-if="from !== 'HomeInput' && isRequireAlterableHeader(field)">
                        <input
                          :checked="selectedRowMappingRule.fieldMappings[field]?.unused ?? false"
                          type="checkbox"
                          class="label-container__checkbox"
                          @change="
                            updateOrderFieldUnused(
                              selectedOrderExcelInfo?.id,
                              selectedRowMappingRule,
                              field,
                              ($event.target as HTMLInputElement).checked
                            )
                          "
                        />
                        <span class="label-container__unused-label">{{ t('取引紐付け：チェックボックス：不使用') }}</span>
                      </template>
                    </div>
                    <template v-if="!(selectedRowMappingRule.fieldMappings[field]?.unused ?? false)">
                      <div class="mapping-compact">
                        <div class="mapping-compact__main">
                          <SelectableField
                            v-if="isEditing && getOrderFieldMappingType(selectedRowMappingRule.fieldMappings[field]) === 'column'"
                            :value="orderMappingToValue(selectedRowMappingRule.fieldMappings[field])"
                            :selected-id="selectedFieldId"
                            :hint="t('取引紐付け：サジェストヒント')"
                            :suggestions="suggestions"
                            @update-value="updateOrderFieldMapping(selectedOrderExcelInfo?.id, selectedRowMappingRule, field, $event)"
                            @update-selected-id="emits('update-selected-field-id', $event)"
                          />
                          <div
                            v-else-if="getOrderFieldMappingType(selectedRowMappingRule.fieldMappings[field]) === 'column'"
                            class="field--non-editable"
                          >
                            {{
                              orderMappingToSummary(selectedRowMappingRule.fieldMappings[field]) ||
                              (isRequireAlterableHeader(field) ? '-' : '')
                            }}
                          </div>
                          <div v-else class="mapping-compact__summary">
                            <template v-if="selectedRowMappingRule.fieldMappings[field]?.type === 'concat'">
                              <template
                                v-for="(part, partIndex) in getConcatOrderFieldMapping(selectedRowMappingRule.fieldMappings[field])"
                                :key="`mapping-summary-${selectedOrderExcelInfo?.id}-${selectedRowMappingRule?.id}-${field}-${partIndex}`"
                              >
                                <span v-if="partIndex > 0" class="mapping-compact__summary-separator">+</span>
                                <span
                                  class="mapping-compact__summary-part"
                                  :class="{ 'mapping-compact__summary-part--literal': part.type === 'literal' }"
                                >
                                  {{ orderMappingToSummary(part) || t('未設定') }}
                                </span>
                              </template>
                            </template>
                            <span v-else class="mapping-compact__summary-part mapping-compact__summary-part--literal">
                              {{
                                orderMappingToSummary(selectedRowMappingRule.fieldMappings[field]) ||
                                (isRequireAlterableHeader(field) ? '-' : '')
                              }}
                            </span>
                          </div>
                        </div>
                        <button
                          v-if="isEditing"
                          type="button"
                          class="mapping-compact__detail-button"
                          :aria-label="t('詳細設定')"
                          @click="toggleOrderFieldMappingDetail(selectedOrderExcelInfo?.id, selectedRowMappingRule?.id, field)"
                        >
                          <Mdi icon="more_horiz" />
                        </button>
                      </div>
                      <div
                        v-if="isEditing && isOrderFieldMappingDetailOpen(selectedOrderExcelInfo?.id, selectedRowMappingRule?.id, field)"
                        class="mapping-editor"
                      >
                        <div class="mapping-editor__type-row">
                          <span class="mapping-editor__type-label">{{ t('種別') }}</span>
                          <select
                            :value="getOrderFieldMappingType(selectedRowMappingRule.fieldMappings[field])"
                            class="mapping-editor__type-select"
                            @change="
                              updateOrderFieldMappingType(selectedOrderExcelInfo?.id, selectedRowMappingRule, field, getSelectValue($event))
                            "
                          >
                            <option value="column">{{ t('列') }}</option>
                            <option value="literal">{{ t('固定値') }}</option>
                            <option value="concat">{{ t('連結') }}</option>
                          </select>
                        </div>
                        <template v-if="getOrderFieldMappingType(selectedRowMappingRule.fieldMappings[field]) === 'literal'">
                          <ConditionDateInput
                            v-if="getOrderFieldMappingDataType(selectedRowMappingRule.fieldMappings[field], field) === 'date'"
                            :model-value="
                              selectedRowMappingRule.fieldMappings[field]?.type === 'literal'
                                ? selectedRowMappingRule.fieldMappings[field]?.value
                                : ''
                            "
                            class="mapping-editor__date-input"
                            @update:model-value="
                              updateOrderFieldLiteralMapping(selectedOrderExcelInfo?.id, selectedRowMappingRule, field, $event)
                            "
                          />
                          <input
                            v-else
                            :value="
                              selectedRowMappingRule.fieldMappings[field]?.type === 'literal'
                                ? selectedRowMappingRule.fieldMappings[field]?.value
                                : ''
                            "
                            class="mapping-editor__literal-input"
                            :type="getOrderFieldLiteralInputType(selectedRowMappingRule.fieldMappings[field], field)"
                            :placeholder="t('固定値')"
                            @input="
                              updateOrderFieldLiteralMapping(
                                selectedOrderExcelInfo?.id,
                                selectedRowMappingRule,
                                field,
                                getInputValue($event)
                              )
                            "
                          />
                        </template>
                        <div
                          v-else-if="getOrderFieldMappingType(selectedRowMappingRule.fieldMappings[field]) === 'concat'"
                          class="mapping-editor__parts"
                        >
                          <div
                            v-for="(part, partIndex) in getConcatOrderFieldMapping(selectedRowMappingRule.fieldMappings[field])"
                            :key="`mapping-part-${selectedOrderExcelInfo?.id}-${selectedRowMappingRule?.id}-${field}-${partIndex}`"
                            class="mapping-editor__part"
                          >
                            <select
                              :value="part.type === 'literal' ? 'literal' : 'column'"
                              class="mapping-editor__part-type"
                              @change="
                                updateConcatPartType(
                                  selectedOrderExcelInfo?.id,
                                  selectedRowMappingRule,
                                  field,
                                  partIndex,
                                  getSelectValue($event)
                                )
                              "
                            >
                              <option value="column">{{ t('列') }}</option>
                              <option value="literal">{{ t('固定値') }}</option>
                            </select>
                            <SelectableField
                              v-if="part.type === 'column'"
                              :value="part.column"
                              :selected-id="selectedFieldId"
                              :hint="t('取引紐付け：サジェストヒント')"
                              :suggestions="suggestions"
                              @update-value="
                                updateConcatPartColumn(selectedOrderExcelInfo?.id, selectedRowMappingRule, field, partIndex, $event)
                              "
                              @update-selected-id="emits('update-selected-field-id', $event)"
                            />
                            <template v-else-if="part.type === 'literal'">
                              <ConditionDateInput
                                v-if="getOrderFieldMappingDataType(part, field) === 'date'"
                                :model-value="part.value"
                                class="mapping-editor__date-input"
                                @update:model-value="
                                  updateConcatPartLiteral(selectedOrderExcelInfo?.id, selectedRowMappingRule, field, partIndex, $event)
                                "
                              />
                              <input
                                v-else
                                :value="part.value"
                                class="mapping-editor__literal-input"
                                :type="getOrderFieldLiteralInputType(part, field)"
                                :placeholder="t('固定値')"
                                @input="
                                  updateConcatPartLiteral(
                                    selectedOrderExcelInfo?.id,
                                    selectedRowMappingRule,
                                    field,
                                    partIndex,
                                    getInputValue($event)
                                  )
                                "
                              />
                            </template>
                            <button
                              type="button"
                              class="mapping-editor__icon-button"
                              :aria-label="t('削除')"
                              @click="removeConcatPart(selectedOrderExcelInfo?.id, selectedRowMappingRule, field, partIndex)"
                            >
                              <Mdi icon="close" />
                            </button>
                            <span v-if="getOrderFieldMappingErrorMessage(part)" class="mapping-editor__part-error">
                              {{ getOrderFieldMappingErrorMessage(part) }}
                            </span>
                          </div>
                          <div class="mapping-editor__part-actions">
                            <button
                              type="button"
                              class="mapping-editor__add-button"
                              @click="addConcatPart(selectedOrderExcelInfo?.id, selectedRowMappingRule, field, 'column')"
                            >
                              <Mdi icon="add" />
                              {{ t('列') }}
                            </button>
                            <button
                              type="button"
                              class="mapping-editor__add-button"
                              @click="addConcatPart(selectedOrderExcelInfo?.id, selectedRowMappingRule, field, 'literal')"
                            >
                              <Mdi icon="add" />
                              {{ t('固定値') }}
                            </button>
                          </div>
                        </div>
                      </div>
                      <span
                        v-if="getOrderFieldMappingErrorMessage(selectedRowMappingRule.fieldMappings[field])"
                        class="linking-container__at--error"
                      >
                        {{ getOrderFieldMappingErrorMessage(selectedRowMappingRule.fieldMappings[field]) }}
                      </span>
                    </template>
                    <template v-else>
                      <span class="linking-container__at--unused">
                        {{ t(getUnusedTextI18nKey(selectedRowMappingRule.fieldMappings[field]?.dataType)) }}
                      </span>
                    </template>
                  </div>
                </div>
              </template>
            </div>

            <div class="linking-container__separator" />

            <div class="linking-container__right">
              <InputExcelOrderStatusInfo
                :rules="getImportOrderStatusRules(selectedRowMappingRule)"
                :fallback-status="getFallbackOrderStatus(selectedRowMappingRule)"
                :suggestions="props.suggestions"
                :is-editing="props.from === 'LinkingInput'"
                @update:rules="updateImportOrderStatusRules(selectedOrderExcelInfo?.id, selectedRowMappingRule, $event)"
                @update:fallback-status="updateFallbackOrderStatus(selectedOrderExcelInfo?.id, selectedRowMappingRule, $event)"
              />
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.linking-container {
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto auto auto;
  align-items: start;
  min-height: 0;

  &__tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }

  &__tab {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    align-items: center;
    gap: 6px;
    max-width: 280px;
    min-height: 34px;
    padding: 4px 10px;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    font-family: Noto Sans JP;
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;
    color: $textDefault;
    background-color: $semanticWhite;

    &--editing {
      grid-template-columns: minmax(0, 1fr) minmax(0, max-content);
    }

    &--deletable {
      grid-template-columns: minmax(0, 1fr) minmax(0, max-content) minmax(0, max-content);
    }

    &--active {
      color: $textDefault;
      background-color: $semanticPrimarySubdued;
      border-color: $borderActive;
    }
  }

  &__tab-label {
    min-width: 0;
    padding: 2px 0;
    border: none;
    overflow: hidden;
    color: inherit;
    font: inherit;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
    background: transparent;
    cursor: pointer;
  }

  &__tab-icon-button {
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    color: $textSecondary;
    background-color: $semanticWhite;
    cursor: pointer;

    :deep(.material-icons) {
      font-size: 18px;
    }

    &--danger {
      color: $semanticError;
    }

    &:disabled {
      color: $textSecondary;
      background-color: $backgroundDefault;
      cursor: not-allowed;
    }
  }

  &__tab-input {
    min-width: 80px;
    width: 100%;
    height: 26px;
    padding: 3px 6px;
    border: 1px solid $borderActive;
    border-radius: 4px;
    color: $textDefault;
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    background-color: $semanticWhite;
  }

  &__row-tabs {
    display: flex;
    align-items: stretch;
    gap: 0;
    min-width: 0;
    overflow-x: auto;
    border-bottom: 1px solid $borderActive;
  }

  &__row-tab {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    align-items: center;
    gap: 6px;
    max-width: 280px;
    min-height: 36px;
    padding: 5px 10px;
    border: 1px solid $borderDefault;
    border-bottom: none;
    border-radius: 6px 6px 0 0;
    color: $textDefault;
    font-family: Noto Sans JP;
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;
    background-color: $backgroundDefault;
    cursor: grab;

    &--editing {
      grid-template-columns: minmax(0, 1fr) max-content max-content;
    }

    &:active {
      cursor: grabbing;
    }

    &--active {
      background-color: $semanticWhite;
      border-color: $borderActive;
      box-shadow: inset 0 2px 0 $semanticPrimary;
      z-index: 1;
    }

    &--dragging {
      opacity: 0.5;
    }

    &--drop-target {
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -2px;
        bottom: 0;
        width: 2px;
        background-color: $semanticPrimary;
        z-index: 1;
        pointer-events: none;
      }
    }
  }

  &__row-tab-end-drop {
    position: relative;
    flex: 0 0 20px;
    min-height: 36px;

    &--active {
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 8px;
        bottom: 0;
        width: 2px;
        background-color: $semanticPrimary;
        z-index: 1;
        pointer-events: none;
      }
    }
  }

  &__row-tab-add-button {
    display: grid;
    place-items: center;
    flex: 0 0 42px;
    min-height: 36px;
    padding: 0;
    border: 1px solid $borderDefault;
    border-bottom: none;
    border-radius: 6px 6px 0 0;
    color: $semanticPrimary;
    background-color: $semanticWhite;
    cursor: pointer;

    :deep(.material-icons) {
      font-size: 20px;
    }
  }

  &__setting {
    display: grid;
    min-width: 0;
  }

  &__setting-header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px;
  }

  &__rule {
    padding: 12px;
    display: grid;
    gap: 16px;
    min-width: 0;
    border: 3px solid $borderDefault;
    border-radius: 4px;
  }

  &__name-field {
    display: grid;
    gap: 4px;
    min-width: 240px;
    max-width: 360px;
  }

  &__name-field--rule {
    max-width: 280px;
  }

  &__name-label {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    color: $textDefault;
  }

  &__name-input {
    min-height: 36px;
    padding: 6px 10px;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: $textDefault;
    background-color: $semanticWhite;
  }

  &__left {
    display: grid;
    gap: 32px;
    grid-template-columns: repeat(auto-fit, minmax(180px, 260px));
    grid-auto-rows: max-content;
    align-content: start;
  }

  &__at {
    display: grid;
    grid-template-rows: repeat(2, fit-content(100%));

    &--content {
      display: grid;
      grid-template-rows: repeat(2, fit-content(100%));
      border-bottom: 1px solid $borderDefault;
    }

    &--label {
      margin-bottom: 4px;
      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 700;
      line-height: 20px;
      color: $textDefault;
    }

    &--unused {
      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 400;
      line-height: 32px;
      color: $semanticWarning;
    }

    &--error {
      min-height: 16px;
      color: $semanticError;
      font-family: Noto Sans JP;
      font-size: 12px;
      font-weight: 700;
      line-height: 16px;
    }
  }

  &__separator {
    height: 1px;
    background-color: $borderDefault;
  }

  &__right {
    min-width: 0;
  }
}

.mapping-compact {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 32px;
  align-items: start;
  gap: 4px;
  min-width: 0;

  &__main {
    min-width: 0;

    :deep(.selectable-input-container) {
      display: block;
      width: 100%;
    }
  }

  &__summary {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    min-height: 32px;
    padding: 4px 0;
    color: $textDefault;
    font-family: Noto Sans JP;
    font-size: 12px;
    line-height: 16px;
    overflow: hidden;
  }

  &__summary-part {
    min-width: 0;
    overflow: hidden;
    color: $textDefault;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__summary-part--literal {
    min-width: 0;
    padding: 2px 6px;
    overflow: hidden;
    border: 1px solid $borderDefault;
    border-radius: 999px;
    background-color: $backgroundDefault;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__summary-separator {
    flex: 0 0 auto;
    color: $textSecondary;
  }

  &__detail-button {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 4px;
    color: $semanticPrimary;
    background-color: transparent;
    cursor: pointer;

    :deep(.material-icons) {
      font-size: 16px;
    }
  }
}

.mapping-editor {
  display: grid;
  gap: 6px;
  min-width: 0;
  padding: 8px;
  border: 1px solid $borderDefault;
  border-radius: 4px;
  background-color: $backgroundDefault;

  &__type-row {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  &__type-label {
    flex: 0 0 auto;
    color: $textDefault;
    font-family: Noto Sans JP;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
  }

  &__type-select,
  &__part-type,
  &__literal-input {
    min-width: 0;
    height: 32px;
    padding: 5px 8px;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    color: $textDefault;
    font-family: Noto Sans JP;
    font-size: 12px;
    line-height: 16px;
    background-color: $semanticWhite;
  }

  &__type-select {
    flex: 0 0 104px;
    width: 104px;
  }

  &__date-input {
    min-width: 0;

    :deep(.condition-date-input__trigger) {
      min-height: 32px;
      font-family: Noto Sans JP;
      font-size: 12px;
      line-height: 16px;
    }
  }

  &__parts {
    display: grid;
    gap: 6px;
    min-width: 0;
  }

  &__part {
    display: grid;
    grid-template-columns: 92px minmax(0, 1fr) 28px;
    align-items: center;
    gap: 4px;
    min-width: 0;

    :deep(.selectable-input-container) {
      display: block;
      width: 100%;
    }

    :deep(.label-container) {
      min-height: 32px;
    }
  }

  &__part-error {
    grid-column: 2 / 3;
    color: $semanticError;
    font-family: Noto Sans JP;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
  }

  &__part-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  &__add-button,
  &__icon-button {
    display: inline-grid;
    grid-auto-flow: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    color: $textDefault;
    font-family: Noto Sans JP;
    font-size: 12px;
    line-height: 16px;
    background-color: $semanticWhite;
    cursor: pointer;

    :deep(.material-icons) {
      font-size: 16px;
    }
  }

  &__add-button {
    min-height: 28px;
    padding: 3px 8px;
    color: $semanticPrimary;
  }

  &__icon-button {
    width: 28px;
    height: 32px;
    padding: 0;
  }
}

.label-container {
  display: flex;
  align-items: center;
  gap: 4px;

  &__checkbox {
    margin-left: 4px;
  }

  &__unused-label {
    font-family: Noto Sans JP;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: $textDefault;
  }
}

.row-conditions {
  --row-conditions-row-height: 32px;

  display: grid;
  gap: 8px;
  padding: 10px;
  border: 1px solid $borderDefault;
  border-radius: 6px;
  background-color: $backgroundDefault;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__title-wrap {
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
  }

  &__title {
    margin: 0;
    color: $textDefault;
    font-family: Noto Sans JP;
    font-size: $fontSizeM;
    font-weight: 700;
    line-height: 20px;
  }

  &__summary {
    color: $textSecondary;
    font-family: Noto Sans JP;
    font-size: 12px;
    line-height: 16px;
  }

  &__body {
    display: grid;
    gap: 6px;
  }

  &__row {
    display: grid;
    grid-template-columns: 32px minmax(140px, 1.3fr) minmax(120px, 1fr) minmax(200px, 1.6fr) 32px;
    align-items: start;
    gap: 8px;
    min-width: 0;
    padding: 8px 10px;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    background-color: $semanticWhite;

    &:focus-within {
      border-color: $borderActive;
      background-color: $semanticWhite;
    }
  }

  &__join-label {
    align-self: center;
    color: $textSecondary;
    font-size: 12px;
    text-align: center;
  }

  &__select-input {
    display: grid;
    grid-template-rows: auto auto;
    min-width: 0;
  }

  &__value-cell {
    display: grid;
    grid-template-columns: 84px minmax(0, 1fr);
    align-items: center;
    gap: 4px;
    min-width: 0;
  }

  &__input {
    min-width: 0;
    height: var(--row-conditions-row-height);
    padding: 5px 8px;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    color: $textDefault;
    font-family: Noto Sans JP;
    font-size: 12px;
    line-height: 16px;
    background-color: $semanticWhite;
  }

  &__actions {
    display: grid;
    place-items: center;
    align-self: start;
    min-height: var(--row-conditions-row-height);
  }

  &__add-button,
  &__icon-button {
    display: inline-grid;
    grid-auto-flow: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    color: $textDefault;
    background-color: $semanticWhite;
    font-family: Noto Sans JP;
    font-size: 12px;
    line-height: 16px;
    cursor: pointer;

    :deep(.material-icons) {
      font-size: 16px;
    }
  }

  &__add-button {
    min-height: 32px;
    padding: 4px 8px;
    color: $semanticPrimary;
  }

  &__icon-button {
    width: 32px;
    height: 32px;
    padding: 0;
  }

  &__error-text {
    grid-column: 1 / -1;
    min-height: 16px;
    color: $semanticError;
    font-family: Noto Sans JP;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
  }

  &__duplicate-error {
    color: $semanticError;
    font-family: Noto Sans JP;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
  }

  &__readonly-text,
  &__no-value,
  &__empty {
    min-width: 0;
    overflow: hidden;
    color: $textSecondary;
    font-family: Noto Sans JP;
    font-size: 12px;
    line-height: 16px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__empty {
    display: grid;
    align-items: center;
    padding: 8px 10px;
    border: 1px dashed $borderDefault;
    border-radius: 4px;
    color: $textSecondary;
    background-color: $semanticWhite;
  }
}
</style>
